import React from 'react';
import Widget from '../../../widgets/Widget';
import WaterNetworkOverlay from '../../../widgets/WaterNetworkOverlay';
import { WidgetArrowsIcon, WidgetDeleteIcon, WidgetDuplicateIcon } from '../../../svg/TemplateIcons';
import { STATUS_ARROW_WIDGET_TYPE } from '../../../widgets/assetStatusUtils';

export const FixedWidthGuide = ({ t, fixedWidthGuide }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      inset: 0,
      width: `${fixedWidthGuide.width}px`,
      height: '100%',
      pointerEvents: 'none',
      border: '2px dashed rgba(255, 255, 255, 0.45)',
      borderRadius: '10px',
      boxSizing: 'border-box',
      backgroundImage:
        'repeating-linear-gradient(45deg, rgba(255,255,255,0.08), rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.03) 8px, rgba(255,255,255,0.03) 16px)',
      backdropFilter: 'blur(0px)',
      opacity: 0.95,
      zIndex: 2,
    }}
  >
    <div
      style={{
        position: 'sticky',
        top: 8,
        left: 8,
        width: 'fit-content',
        maxWidth: 'calc(100% - 16px)',
        margin: 8,
        padding: '6px 10px',
        borderRadius: 999,
        background: 'rgba(0,0,0,0.45)',
        color: 'rgba(255,255,255,0.92)',
        fontSize: 12,
        lineHeight: 1.2,
        letterSpacing: 0.2,
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
        border: '1px solid rgba(255,255,255,0.18)',
      }}
    >
      {t.fixedWidthGuideLabel ||
        'Fixed width area (keep widgets inside to enable Fixed Width)'}
    </div>
  </div>
);

export const EditDashboardCanvas = ({
  t,
  dashboard,
  devices,
  validDatastreams,
  divs,
  isFixedWidthMode,
  fixedWidthGuide,
  isPercentageLayout,
  editableWaterNetworkState,
  multiSelectEnabled,
  selectedWidgetIdSet,
  selectedGroupDragOffset,
  selectionRect,
  selectedWidgetBounds,
  selectedWidgetIds,
  duplicateSelectedGroup,
  removeSelectedGroup,
  toggleSelectedGroupFreeMovement,
  selectedGroupFreeMovementEnabled,
  widgetLiveProps,
  edit,
  defaultDivHeight,
  defaultDivWidth,
  layoutMode,
  handleDashboardDragOver,
  drop,
  handleEditablePointerDown,
  handleEditableClickCapture,
  startSelectedGroupDrag,
  getWidgetRectPx,
  findDatastreamByReference,
  formatPinForTitle,
  preserveTitleSpaces,
  resizeWidget,
  setEdit,
  setDeviceDatastream,
  handleTerminalCleared,
  duplicate,
  remove,
  updateWidgetLivePropsById,
  commitWidgetPropsById,
  defaultWidgetPropsByType,
}) => (
  <div
    className='diverted-dashboard'
    id='diverted'
    dir='ltr'
    onDragOver={handleDashboardDragOver}
    onDrop={drop}
  >
    {isFixedWidthMode && fixedWidthGuide?.width ? (
      <FixedWidthGuide t={t} fixedWidthGuide={fixedWidthGuide} />
    ) : null}

    {divs}
    <div
      className={`editable-dashboard${multiSelectEnabled ? ' editable-dashboard--multi-select' : ''}`}
      id='editable'
      dir='ltr'
      onDragOver={handleDashboardDragOver}
      onDrop={drop}
      onPointerDown={handleEditablePointerDown}
      onClickCapture={handleEditableClickCapture}
    >
      <WaterNetworkOverlay
        width={editableWaterNetworkState?.width}
        height={editableWaterNetworkState?.height}
        connections={editableWaterNetworkState?.connections || []}
      />
      {(dashboard?.widgets || []).map(widget => {
        const matchDevice = (devices || []).find(d => Number(d?.id) === Number(widget.device_id));
        const datastreamSource = matchDevice ? (matchDevice.template?.datastreams || []).filter(ds => ds) : validDatastreams;
        let pinToDisplay = null;
        const percentageRenderRect = isPercentageLayout ? getWidgetRectPx(widget) : null;
        const linkedDatastream = findDatastreamByReference(
          datastreamSource,
          widget.data_id ?? widget.dataId,
          widget.data_pin ?? widget.dataPin,
        );
        if (linkedDatastream) {
          pinToDisplay = linkedDatastream.pin;
        } else if ((widget.type === 'chart' || widget.type === 'table') && widget.extra_props?.curves?.length > 0) {
          const firstCurvePin = widget.extra_props.curves[0].pin;
          if (firstCurvePin && firstCurvePin !== 'Pin') {
            pinToDisplay = firstCurvePin;
          }
        }

        const cleanStoredTitle = widget.title.replace(/^\[.*?\]\s*/, '').trim();
        const pinFormatted = formatPinForTitle(pinToDisplay);
        const displayTitleForEditMode = pinFormatted ? `${pinFormatted} ${cleanStoredTitle}` : cleanStoredTitle;
        const dynamicProps = {
          ...(defaultWidgetPropsByType?.[widget.type] || {}),
          ...widget.extra_props,
          ...((widget.type === 'slider' && widgetLiveProps?.[widget.id]) ? widgetLiveProps[widget.id] : {}),
        };
        if (
          widget.type === 'slider' &&
          edit.open &&
          String(edit.id) === String(widget.id)
        ) {
          Object.assign(dynamicProps, edit.props || {});
        }
        const settingsProps = { ...(widget.extra_props || {}) };
        if (settingsProps.labelFontSize === undefined && dynamicProps.labelFontSize !== undefined) {
          settingsProps.labelFontSize = dynamicProps.labelFontSize;
        }
        if (settingsProps.valueFontSize === undefined && dynamicProps.valueFontSize !== undefined) {
          settingsProps.valueFontSize = dynamicProps.valueFontSize;
        }

        return (
          <Widget
            transparentBackground={widget.transparentBackground}
            draggable={!(multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id)))}
            title={preserveTitleSpaces(displayTitleForEditMode)}
            dataId={widget.data_id}
            resizeWidget={resizeWidget}
            resize={true}
            id={widget.id}
            key={widget.id}
            deviceId={widget.device_id}
            width={widget.width}
            height={widget.height}
            type={widget.type}
            selected={multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id))}
            dragOffset={multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id))
              ? selectedGroupDragOffset
              : null}
            dataPin={linkedDatastream?.pin || widget.data_pin || widget.dataPin || pinToDisplay || ''}
            decimals={linkedDatastream?.decimals || ''}
            defaultDivHeight={defaultDivHeight}
            position='absolute'
            layoutMode={layoutMode}
            setEdit={setEdit}
            devices={devices}
            setDeviceDatastream={devices?.length > 0 ? setDeviceDatastream : null}
            top={widget.top}
            left={widget.left}
            extraProps={dynamicProps}
            settingsExtraProps={widget.type === 'slider' ? dynamicProps : settingsProps}
            onTerminalCleared={handleTerminalCleared}
            defaultDivWidth={defaultDivWidth}
            renderRect={percentageRenderRect}
            duplicate={duplicate}
            remove={remove}
            datastreams={datastreamSource}
            preferDatastreamDefaultValue
            onWidgetPropsChange={widget.type === 'slider'
              ? (partialProps) => updateWidgetLivePropsById(widget.id, partialProps, { syncEdit: true })
              : widget.type === STATUS_ARROW_WIDGET_TYPE
                ? (partialProps) => commitWidgetPropsById(widget.id, partialProps, { syncEdit: true })
              : undefined}
          />
        );
      })}
      {multiSelectEnabled && selectionRect ? (
        <div
          className="dashboard-selection-marquee"
          style={{
            left: `${selectionRect.left}px`,
            top: `${selectionRect.top}px`,
            width: `${selectionRect.width}px`,
            height: `${selectionRect.height}px`,
          }}
        />
      ) : null}
      {multiSelectEnabled && selectedWidgetBounds && selectedWidgetIds.length > 0 ? (
        <div
          className="dashboard-selection-bounds"
          onPointerDown={startSelectedGroupDrag}
          style={{
            left: `${selectedWidgetBounds.left}px`,
            top: `${selectedWidgetBounds.top}px`,
            width: `${selectedWidgetBounds.width}px`,
            height: `${selectedWidgetBounds.height}px`,
            transform: selectedGroupDragOffset
              ? `translate(${selectedGroupDragOffset.x || 0}px, ${selectedGroupDragOffset.y || 0}px)`
              : undefined,
          }}
        >
          <div
            className="dashboard-selection-actions"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={{
              top: selectedWidgetBounds.top < 48 ? '6px' : '-42px',
              right: selectedWidgetBounds.width < 118 ? 'auto' : 0,
              left: selectedWidgetBounds.width < 118 ? 0 : 'auto',
            }}
          >
            <button
              type="button"
              className={`dashboard-selection-action${selectedGroupFreeMovementEnabled ? ' is-active' : ''}`}
              onClick={toggleSelectedGroupFreeMovement}
              aria-label={selectedGroupFreeMovementEnabled ? 'Disable free movement for selected widgets' : 'Enable free movement for selected widgets'}
              title={selectedGroupFreeMovementEnabled ? 'Disable free movement for selected widgets' : 'Enable free movement for selected widgets'}
            >
              <WidgetArrowsIcon />
            </button>
            <button
              type="button"
              className="dashboard-selection-action"
              onClick={duplicateSelectedGroup}
              aria-label="Duplicate selected widgets"
              title="Duplicate selected widgets"
            >
              <WidgetDuplicateIcon />
            </button>
            <button
              type="button"
              className="dashboard-selection-action dashboard-selection-action--danger"
              onClick={removeSelectedGroup}
              aria-label="Delete selected widgets"
              title="Delete selected widgets"
            >
              <WidgetDeleteIcon color={'var(--root-background)'} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  </div>
);

export const ViewDashboardCanvas = ({
  dashboard,
  devices,
  validDatastreams,
  did,
  tid,
  isFixedWidthMode,
  fixedViewWidth,
  isPercentageLayout,
  viewPercentageCanvas,
  viewWaterNetworkState,
  defaultDivHeight,
  defaultDivWidth,
  layoutMode,
  getWidgetRectPx,
  findDatastreamByReference,
  preserveTitleSpaces,
  resizeWidget,
  handleTerminalCleared,
  duplicate,
  remove,
  defaultWidgetPropsByType,
}) => (
  <div className='diverted-dashboard' id='diverted-view'
    dir='ltr'
    style={{
      width: (isFixedWidthMode && fixedViewWidth) ? `${fixedViewWidth}px` : '100%',
      position: 'relative',
      justifyContent: 'flex-start',
      alignContent: 'flex-start',
      overflowX: isPercentageLayout ? 'hidden' : 'auto',
      overflowY: 'auto'
    }}>
    <div
      style={
        isPercentageLayout
          ? {
            position: 'relative',
            width: '100%',
            height: viewPercentageCanvas.height > 0 ? `${viewPercentageCanvas.height}px` : '100%',
            minWidth: '100%',
            minHeight: '100%',
          }
          : undefined
      }
    >
      <WaterNetworkOverlay
        width={viewWaterNetworkState?.width}
        height={viewWaterNetworkState?.height}
        connections={viewWaterNetworkState?.connections || []}
      />
      {(dashboard?.widgets || []).map(widget => {
        const matchDevice = (devices || []).find(d => Number(d?.id) === Number(widget.device_id));
        const datastreamSource = matchDevice ? (matchDevice.template?.datastreams || []).filter(ds => ds) : validDatastreams;
        const percentageRenderRect = isPercentageLayout ? getWidgetRectPx(widget) : null;
        const linkedDatastream = findDatastreamByReference(
          datastreamSource,
          widget.data_id ?? widget.dataId,
          widget.data_pin ?? widget.dataPin,
        );

        const dynamicProps = {
          ...(defaultWidgetPropsByType?.[widget.type] || {}),
          ...widget.extra_props,
        };
        const displayTitleForViewMode = widget.title.replace(/^\[.*?\]\s*/, '').trim();

        return (<Widget
          transparentBackground={widget.transparentBackground}
          draggable={false}
          title={preserveTitleSpaces(displayTitleForViewMode)}
          did={matchDevice ? matchDevice.did : did}
          deviceId={widget.device_id}
          tid={matchDevice ? matchDevice.template?.tid : tid}
          devices={devices}
          resizeWidget={resizeWidget} resize={false} id={widget.id} key={widget.id}
          dataId={widget.data_id}
          width={widget.width} height={widget.height} type={widget.type}
          dataPin={linkedDatastream?.pin || widget.data_pin || widget.dataPin || ''}
          decimals={linkedDatastream?.decimals || ''}
          defaultDivHeight={defaultDivHeight} position={'absolute'}
          layoutMode={layoutMode}
          top={widget.top} left={widget.left} extraProps={dynamicProps}
          onTerminalCleared={handleTerminalCleared}
          defaultDivWidth={defaultDivWidth}
          renderRect={percentageRenderRect}
          duplicate={duplicate} remove={remove} datastreams={datastreamSource} />);
      })}
    </div>
  </div>
);
